import { useQuery } from '@tanstack/react-query';

import ReservationService from '~/services/ReservationService';

export const RATE_PLAN_ACTIVE_STATUS = 'content-approved';

export interface RatePlan {
  id: string;
  name?: string;
  rate_plan_code?: string;
  source?: string;
  status?: string;
  price?: number | null;
}

export const ratePlanQueryKeys = {
  all: ['ratePlans'] as const,
  lists: () => [...ratePlanQueryKeys.all, 'list'] as const,
  list: (vendorAccountId: string) => [...ratePlanQueryKeys.lists(), vendorAccountId] as const,
};

interface RoomRate {
  id: string;
  rate_plan: { id: string };
}

interface RoomType {
  id: string;
  room_rates: Array<RoomRate>;
}

interface Property {
  id: string;
  room_types: Array<RoomType>;
}

interface RateFetchTask {
  ratePlanId: string;
  propertyId: string;
  roomTypeId: string;
  roomRateId: string;
}

interface RateFetchResult {
  ratePlanId: string;
  minCost: number | null;
}

const RATE_FETCH_BATCH_SIZE = 100;

/**
 * TEMPORARY: This function fetches the cheapest base rate per plan by making
 * individual requests per room-rate combination. Requests are batched sequentially
 * (RATE_FETCH_BATCH_SIZE at a time) to avoid overwhelming svc-res.
 *
 * TODO: Replace with a bulk BE endpoint that returns the cheapest base rate and
 * its info directly, eliminating the need for per-room-rate fetches.
 */
async function resolveCheapestPricePerPlan(
  properties: Array<Property>,
  ratePlanIds: Set<string>,
): Promise<Record<string, number>> {
  const tasks: Array<RateFetchTask> = [];

  for (const property of properties) {
    for (const roomType of property.room_types) {
      for (const roomRate of roomType.room_rates) {
        if (!ratePlanIds.has(roomRate.rate_plan.id)) {
          continue;
        }
        tasks.push({
          ratePlanId: roomRate.rate_plan.id,
          propertyId: property.id,
          roomTypeId: roomType.id,
          roomRateId: roomRate.id,
        });
      }
    }
  }

  const fetchRate = async (task: RateFetchTask): Promise<RateFetchResult> => {
    const ratesResponse = await ReservationService.getRoomRateDates(task.propertyId, task.roomTypeId, task.roomRateId);
    const dates = ratesResponse?.result ?? [];
    const costs = dates.map((d) => d.cost_amount).filter((c): c is number => c != null && c > 0);
    const minCost = costs.length > 0 ? Math.min(...costs) : null;
    return { ratePlanId: task.ratePlanId, minCost };
  };

  // Process in sequential batches to avoid overwhelming svc-res
  const allResults: Array<PromiseSettledResult<RateFetchResult>> = [];
  for (let i = 0; i < tasks.length; i += RATE_FETCH_BATCH_SIZE) {
    const batch = tasks.slice(i, i + RATE_FETCH_BATCH_SIZE);
    const batchResults = await Promise.allSettled(batch.map(fetchRate));
    allResults.push(...batchResults);
  }

  const priceMap: Record<string, number> = {};
  for (const result of allResults) {
    if (result.status !== 'fulfilled' || result.value.minCost == null) {
      continue;
    }
    const { ratePlanId, minCost } = result.value;
    if (priceMap[ratePlanId] == null || minCost < priceMap[ratePlanId]) {
      priceMap[ratePlanId] = minCost;
    }
  }

  return priceMap;
}

export function useGetPropertyRatePlans(vendorAccountId: string | undefined) {
  return useQuery<Array<RatePlan>>({
    queryKey: ratePlanQueryKeys.list(vendorAccountId ?? ''),
    queryFn: async () => {
      const [ratePlansResponse, propertiesResponse] = await Promise.all([
        ReservationService.getRatePlans(vendorAccountId),
        ReservationService.getProperties(vendorAccountId).catch(() => ({ result: [] })),
      ]);

      const ratePlans: Array<RatePlan> = ratePlansResponse.result ?? [];
      const properties: Array<Property> = propertiesResponse.result ?? [];

      if (properties.length === 0) {
        return ratePlans;
      }

      const activeIds = new Set(
        ratePlans.filter((p) => p.status?.toLowerCase() === RATE_PLAN_ACTIVE_STATUS).map((p) => p.id),
      );

      const priceMap = await resolveCheapestPricePerPlan(properties, activeIds);

      return ratePlans.map((plan) => ({
        ...plan,
        price: priceMap[plan.id] ?? null,
      }));
    },
    enabled: !!vendorAccountId,
  });
}
